import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ScrollProgress from './components/ScrollProgress';
import StructuredData from './components/StructuredData';
import CookieConsent from './components/CookieConsent';
import PageTransition from './components/PageTransition';
import Home from './pages/Home';
import './assets/css/main.css';

// Code-Splitting: weniger genutzte Routen werden lazy geladen
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ImpressumPage = lazy(() => import('./pages/ImpressumPage'));
const DatenschutzPage = lazy(() => import('./pages/DatenschutzPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Suspense-Fallback (zeigt sich nur kurz beim ersten Aufruf einer neuen Route)
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div
      className="w-10 h-10 rounded-full border-4 border-wood-200 border-t-wood-600 dark:border-wood-700 dark:border-t-gold-400 animate-spin"
      role="status"
      aria-label="Wird geladen"
    />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Suspense key={location.pathname} fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PageTransition>
                <Home />
              </PageTransition>
            }
          />
          <Route
            path="/ueber-uns"
            element={
              <PageTransition>
                <AboutPage />
              </PageTransition>
            }
          />
          <Route
            path="/leistungen"
            element={
              <PageTransition>
                <ServicesPage />
              </PageTransition>
            }
          />
          <Route
            path="/galerie"
            element={
              <PageTransition>
                <GalleryPage />
              </PageTransition>
            }
          />
          <Route
            path="/kontakt"
            element={
              <PageTransition>
                <ContactPage />
              </PageTransition>
            }
          />
          <Route
            path="/impressum"
            element={
              <PageTransition>
                <ImpressumPage />
              </PageTransition>
            }
          />
          <Route
            path="/datenschutz"
            element={
              <PageTransition>
                <DatenschutzPage />
              </PageTransition>
            }
          />
          <Route
            path="*"
            element={
              <PageTransition>
                <NotFoundPage />
              </PageTransition>
            }
          />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const AppContent = () => {
  return (
    <>
      {/* Diese Komponenten arbeiten mit position:fixed und müssen
          OUTSIDE des app-container hängen, damit kein Eltern-Transform
          ihren containing-block kapert. */}
      <ScrollProgress />
      <CookieConsent />
      <StructuredData />

      <div className="min-h-screen flex flex-col">
        <ScrollToTop />
        <Navbar />
        <main className="flex-1">
          <AnimatedRoutes />
        </main>
        <Footer />
      </div>
    </>
  );
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
